import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import './UrgencyBadge.styles.css';

export type UrgencyLevel = 'urgent' | 'high' | 'medium' | 'low';

interface UrgencyBadgeProps {
  level: UrgencyLevel;
  isOverdue?: boolean;
  interactive?: boolean;
  onUrgencyChange?: (newLevel: UrgencyLevel) => void;
  disabled?: boolean;
  className?: string;
}

const URGENCY_OPTIONS: { value: UrgencyLevel; label: string }[] = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'Haute' },
  { value: 'medium', label: 'Moyenne' },
  { value: 'low', label: 'Basse' },
];

const LABELS: Record<UrgencyLevel, string> = {
  urgent: 'Urgent',
  high: 'Haute',
  medium: 'Moyenne',
  low: 'Basse',
};

export const UrgencyBadge: React.FC<UrgencyBadgeProps> = ({
  level,
  isOverdue = false,
  interactive = false,
  onUrgencyChange,
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Update position when dropdown opens
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setCoords({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
  }, [isOpen]);

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handleClick = (e: MouseEvent) => {
      if (triggerRef.current && !triggerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [isOpen]);

  // Close on scroll
  useEffect(() => {
    if (!isOpen) return;

    const handleScroll = () => setIsOpen(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [isOpen]);

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (value: UrgencyLevel) => {
    onUrgencyChange?.(value);
    setIsOpen(false);
  };

  const label = LABELS[level] || 'Moyenne';
  const badgeClass = `urgency-badge urgency-${level} ${isOverdue ? 'is-overdue' : ''} ${className}`;

  // Non-interactive badge
  if (!interactive) {
    return (
      <span className={badgeClass} title={isOverdue ? 'En retard' : undefined}>
        {label}
        {isOverdue && <span className="urgency-overdue-dot" />}
      </span>
    );
  }

  // Interactive badge with dropdown
  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`${badgeClass} urgency-interactive`}
        onClick={handleToggle}
        disabled={disabled}
        title={isOverdue ? 'En retard' : undefined}
      >
        {label}
        {isOverdue && <span className="urgency-overdue-dot" />}
        <ChevronDownIcon className="urgency-chevron" />
      </button>

      {isOpen && ReactDOM.createPortal(
        <div
          className="urgency-dropdown"
          style={{ top: coords.top, left: coords.left }}
          onClick={(e) => e.stopPropagation()}
        >
          {URGENCY_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={`urgency-dropdown-item ${level === opt.value ? 'active' : ''}`}
              onClick={() => handleSelect(opt.value)}
            >
              <span className={`urgency-dot urgency-dot-${opt.value}`} />
              {opt.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
};
